import { derived, type Readable } from 'svelte/store';

/**
 * Waits for stores to meet a condition and returns a Promise that resolves with the result
 * @param stores Array of Svelte stores to subscribe to
 * @param condition Function that checks if all store values meet the desired condition
 * @param transform Optional function to transform the store values before resolving
 * @returns A Promise that resolves when the condition is met for all stores
 */
export async function waitForStoreCondition<T extends Readable<any>[], R = any>(
	stores: [...T],
	condition: (values: { [K in keyof T]: T[K] extends Readable<infer U> ? U : never }) => boolean,
	transform?: (values: { [K in keyof T]: T[K] extends Readable<infer U> ? U : never }) => R,
): Promise<R | undefined> {
	const storeValues = Array.from({ length: stores.length });
	const unsubscribes: (() => void)[] = [];

	return new Promise((resolve: (value: R | undefined) => void) => {
		for (let i = 0; i < stores.length; i++) {
			const store = stores[i];
			unsubscribes[i] = store.subscribe((value) => {
				storeValues[i] = value;

				if (condition(storeValues as any)) {
					for (const unsubscribe of unsubscribes) {
						unsubscribe?.();
					}
					resolve(transform ? transform(storeValues as any) : storeValues as unknown as R);
				}
			});
		}
	});
}

// Taken from https://svelte.dev/playground/8ab0d78cc9c84582a693ef9d520f461f
export function asyncDerived<Values, Result>(
	stores: Readable<Values> | Readable<Values>[],
	callback: (values: Values) => Result | Promise<Result>,
	initialValue?: Result,
): Readable<Result> {
	let guard: object | undefined;

	return derived(
		stores,
		($stores: Values | Values[], set: (value: Result) => void) => {
			const inner = (guard = {});

			if (initialValue !== undefined) {
				set(initialValue);
			}

			Promise.resolve(callback($stores as Values)).then((value) => {
				if (guard === inner) {
					set(value);
				}
			});
		},
		initialValue ?? undefined,
	);
}
